"use client";

import { useState } from "react";
import { Plane, Building2, Car, PartyPopper, MapPin, Wine } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  id: 1,
  label: "Airport Transfers",
  icon: Plane,
  description: "Reliable and punctual airport transportation to JFK, LGA, EWR, BDL, HPN, and TEB. Professional drivers ensure you arrive on time for your flight."
}, {
  id: 2,
  label: "City-to-City Rides",
  icon: Building2,
  description: "Comfortable long-distance travel between Connecticut, New York City, Long Island, and New Jersey. Perfect for business trips or leisurely journeys."
}, {
  id: 3,
  label: "Corporate Travel",
  icon: Car,
  description: "Premium transportation solutions for business professionals. Our luxury fleet ensures you arrive at meetings and events in style and comfort."
}, {
  id: 4,
  label: "Event & Night Out",
  icon: PartyPopper,
  description: "Make your special occasions memorable with our event transportation services. Safe, reliable rides for nights out and celebrations."
}, {
  id: 5,
  label: "Long Distance Travel",
  icon: MapPin,
  description: "Extended journeys across the Northeast corridor and beyond. Comfortable, reliable transportation for longer trips with professional service throughout."
}, {
  id: 6,
  label: "Winery Tours",
  icon: Wine,
  description: "Enjoy a luxurious winery tour experience across Connecticut and the Northeast region. Our professional chauffeurs provide safe, comfortable transportation while you relax and savor the finest wines without worrying about driving."
}];
export default function OurTransportationServices() {
  const [selectedService, setSelectedService] = useState<number | null>(null);
  const {
    ref: gridRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section className="relative bg-background py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="diagonal-lines" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
              <line x1="0" y1="40" x2="40" y2="0" stroke="currentColor" strokeWidth="1" className="text-muted" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#diagonal-lines)" />
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto">
        {}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-display font-bold text-foreground mb-4">
            Our Transportation Services
          </h2>
          <p className="text-foreground/70 text-lg max-w-2xl mx-auto">
            Discover our comprehensive range of premium transportation solutions designed for your convenience and comfort.
          </p>
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, i) => {
          const Icon = service.icon;
          const delay = i * 100;
          return <Card key={service.id} data-index={i} className={`group cursor-pointer transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:-translate-y-1 hover:shadow-strong focus-ring ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2.5"}`} style={{
            transitionDelay: isVisible ? `${delay}ms` : "0ms"
          }} onClick={() => setSelectedService(service.id)} role="button" tabIndex={0} onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setSelectedService(service.id);
            }
          }}>
                <CardContent className="flex flex-col items-center text-center gap-4 p-8">
                  {}
                  <div className="w-16 h-16 rounded-lg bg-secondary/10 flex items-center justify-center transition-transform duration-[var(--duration-medium)] ease-[var(--ease-spring)] group-hover:scale-105">
                    <Icon className="w-8 h-8 text-foreground-secondary" strokeWidth={1.5} />
                  </div>

                  {}
                  <h3 className="text-xl font-display font-semibold text-foreground relative">
                    {service.label}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] group-hover:w-full" />
                  </h3>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>

      {}
      <Sheet open={selectedService !== null} onOpenChange={open => !open && setSelectedService(null)}>
        <SheetContent side="bottom" className="h-auto max-h-[80vh] overflow-y-auto">
          {selectedService && <>
              <SheetHeader>
                <SheetTitle className="text-2xl font-display">
                  {services.find(s => s.id === selectedService)?.label}
                </SheetTitle>
                <SheetDescription className="text-base text-foreground/80 mt-4">
                  {services.find(s => s.id === selectedService)?.description}
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-4">
                <div className="bg-muted/10 rounded-lg p-4">
                  <h4 className="font-semibold text-foreground mb-2">Why Choose Us?</h4>
                  <ul className="space-y-2 text-sm text-foreground/80">
                    <li className="flex items-start gap-2">
                      <span className="text-accent">•</span>
                      <span>24/7 availability for your convenience</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-accent">•</span>
                      <span>Luxury fleet of vehicles including sedans, SUVs, and luxury SUVs</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-accent">•</span>
                      <span>Professional and experienced drivers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-accent">•</span>
                      <span>Reliable and punctual service</span>
                    </li>
                  </ul>
                </div>
              </div>
            </>}
        </SheetContent>
      </Sheet>
    </section>;
}